import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/auth";

export default function Organizations() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/ops/organizations").then((r) => setItems(r.data.items || [])).catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail)));
  }, []);
  return (
    <div className="space-y-4" data-testid="ops-organizations-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Organizations</h1>
      </div>
      {err && <div className="text-sm text-red-700">{err}</div>}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left px-4 py-2">Organisation</th><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Contact</th><th className="text-left px-4 py-2">Linked ecosystem entities</th><th className="text-left px-4 py-2">Open</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No active organisations yet.</td></tr>}
            {items.map((o) => (
              <tr key={o.id} className="border-t" data-testid="ops-organization-row">
                <td className="px-4 py-3 text-slate-900">{o.organization_name}</td>
                <td className="px-4 py-3">{o.organization_type}</td>
                <td className="px-4 py-3">{o.primary_contact_email}</td>
                <td className="px-4 py-3">{(o.linked_ecosystem_entity_ids || []).length}</td>
                <td className="px-4 py-3"><Link to={`/ops/organizations/${o.id}`} className="text-slate-900 font-medium">Open →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        Approval to partner does not automatically make organization content patient-visible. Each
        offering must be created and reviewed through the existing Ecosystem governance
        (<Link to="/ecosystem" className="underline">/ecosystem</Link>) before it can become
        patient-visible.
      </p>
    </div>
  );
}

export function OrganizationDetail() {
  const { orgId } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [entityId, setEntityId] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get(`/ops/organizations/${orgId}`).then((r) => setO(r.data.organization)).catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);
  const link = async (e) => {
    e.preventDefault();
    if (!entityId.trim()) return;
    setBusy(true);
    try {
      await api.post(`/ops/organizations/${orgId}/link-entity`, { entity_id: entityId.trim() });
      toast.success("Ecosystem entity linked.");
      setEntityId(""); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not link entity.");
    } finally { setBusy(false); }
  };
  if (err) return <div className="text-sm text-red-700">{err}</div>;
  if (!o) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="ops-organization-detail">
      <Link to="/ops/organizations" className="text-sm text-slate-500">← Organizations</Link>
      <div className="bg-white rounded-xl border p-6">
        <div className="text-xs text-slate-500 uppercase mb-1">{o.organization_type}</div>
        <h1 className="text-2xl font-semibold text-slate-900">{o.organization_name}</h1>
        <div className="text-sm text-slate-500 mb-4">{o.primary_contact_email} · {o.location || "no location"}</div>
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{o.description}</p>
        <div className="mt-4 text-xs text-slate-500">Proposed domains: {(o.proposed_domain_keys || []).join(", ") || "—"}</div>
      </div>
      <div className="bg-white rounded-xl border p-6 space-y-3">
        <h2 className="text-sm font-medium text-slate-600">Linked Ecosystem entities</h2>
        {(o.linked_ecosystem_entity_ids || []).length === 0 && <p className="text-sm text-slate-500">No entities linked yet.</p>}
        <ul className="text-sm space-y-1">
          {(o.linked_ecosystem_entity_ids || []).map((eid) => (
            <li key={eid} className="flex items-center justify-between" data-testid="ops-linked-entity"><code className="text-xs">{eid}</code><Link to={`/ecosystem/entities/${eid}`} className="text-slate-900 font-medium">Open in Ecosystem →</Link></li>
          ))}
        </ul>
        <form onSubmit={link} className="flex gap-2 mt-3">
          <input value={entityId} onChange={(e) => setEntityId(e.target.value)} placeholder="Existing ecosystem entity id" className="flex-1 text-sm border rounded px-3 py-2" data-testid="ops-link-entity-input" />
          <button disabled={busy || !entityId.trim()} type="submit" className="btc-btn-primary text-sm" data-testid="ops-link-entity-submit">Link</button>
        </form>
        <p className="text-xs text-slate-500">
          Ecosystem entities are created + governed inside{" "}
          <Link to="/ecosystem/entities/new" className="underline">/ecosystem/entities/new</Link>. Link the returned id here to
          associate the entity with this organization. Patient visibility remains governed by
          existing Ecosystem workflow (B.1–B.6).
        </p>
      </div>
    </div>
  );
}
